/**
 * Timings and limits that a person notices and the help quotes.
 *
 * They lived next to the code that enforces them (API, web, app), each as a
 * literal. The help (packages/data/src/help) must never repeat a number in
 * its own words — a changed limit would leave the help saying the old one —
 * so the enforcing code and the help read the same constant from here.
 * Photo limits stay in validation.ts, deletion periods in retention.ts.
 */

/** Digits of an e-mail sign-in code. */
export const LOGIN_CODE_LENGTH = 6;
/** How long a sign-in code is valid. */
export const LOGIN_CODE_TTL_MINUTES = 15;
/** Wrong guesses per code before a new one is needed. */
export const LOGIN_CODE_MAX_ATTEMPTS = 5;
/** Codes per address within LOGIN_CODE_SEND_WINDOW_MINUTES … */
export const LOGIN_CODE_SENDS_PER_WINDOW = 3;
export const LOGIN_CODE_SEND_WINDOW_MINUTES = 15;
/** … and per day. */
export const LOGIN_CODE_SENDS_PER_DAY = 10;
/** The sign-in form offers "Code erneut senden" after this many seconds. */
export const LOGIN_CODE_RESEND_SECONDS = 60;
/** A session lasts this long after its last renewal (sliding). */
export const SESSION_DAYS = 90;
/** How long the "Rückgängig" toast stays after a write. */
export const UNDO_WINDOW_SECONDS = 10;
/** The native app keeps its offline copy of the club's data at most this long after the last fetch. */
export const OFFLINE_CACHE_DAYS = 7;
/** First retry of a report waiting in the app's outbox … */
export const OUTBOX_FIRST_RETRY_SECONDS = 5;
/** … doubling up to this. */
export const OUTBOX_MAX_RETRY_MINUTES = 60;
/**
 * Invitation mails from a member import are sent in steps, at most this many
 * an hour (invitationTimer, every five minutes). ACS takes 100 mails an hour
 * per subscription on a custom domain, shared with the sign-in codes and other
 * apps; the rest of the hour stays for the codes (0020, 15.10.2026).
 */
export const INVITATIONS_PER_HOUR = 60;
/**
 * Further sign-in addresses of one person besides the first (0023). Each one
 * is a door into every club of that person, so a handful, not a list.
 */
export const MAX_ADDITIONAL_EMAILS = 3;
/**
 * Confirmation mails to ONE address that is not yet anybody's, per day, from
 * all persons together (0023, „Weitere Adresse“): a member who types a
 * stranger's address must not be able to fill that mailbox. The person's own
 * sends count as well, with the sign-in code's numbers (3 per quarter-hour,
 * 10 per day), and they share the ACS budget with everything else (rule 48).
 */
export const EMAIL_CONFIRM_SENDS_PER_ADDRESS_PER_DAY = 3;

/**
 * Settings a new club starts with (spec 5, `settings`; the API's
 * DEFAULT_SETTINGS and the demo seed use these). The admin changes them per
 * club; the help quotes the default and says it can differ.
 */
/** Nearby radius as a fraction of the drawing's width. */
export const DEFAULT_DUPLICATE_RADIUS = 0.085;
/** Days until an unreviewed report counts as overdue in the inbox. */
export const DEFAULT_REVIEW_DUE_DAYS = 7;
